import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";

const values = [
  {
    icon: "ri-flashlight-line",
    title: "及时",
    desc: "每日追踪影视行业动态，第一时间带来新片定档、剧集开播与票房盘点。",
  },
  {
    icon: "ri-scales-3-line",
    title: "客观",
    desc: "评分与评价综合多方参考，尽量还原作品真实的观感与口碑。",
  },
  {
    icon: "ri-book-open-line",
    title: "深度",
    desc: "不止于资讯搬运，更提供剧情梳理、人物分析与创作背景解读。",
  },
  {
    icon: "ri-heart-3-line",
    title: "用心",
    desc: "每个推荐都经过筛选，只把真正值得一看的作品放到你面前。",
  },
];

export default function AboutPage() {
  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = ((formData.get("website_alt") as string | null) ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setSuccessMsg("感谢你的反馈，我们会认真阅读！");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(
        "https://readdy.ai/api/form/db7k2cgmkl3spn8sm9kg",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: payload.toString(),
        }
      );
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setSuccessMsg("感谢你的反馈，我们会认真阅读！");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim() && !isSpam
            ? serverMsg
            : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <SiteLayout>
      <Seo
        title="关于唐心 - 影视资讯与作品推荐平台"
        description="了解唐心影视的品牌故事、内容范围与联系方式，我们专注为影视爱好者提供最新影视资讯与优质作品推荐。"
        keywords="唐心,关于唐心,影视资讯平台,影视推荐,品牌故事"
        canonicalPath="/about"
      />

      {/* Hero band */}
      <section className="w-full px-4 md:px-6 pt-6">
        <div className="relative overflow-hidden rounded-xl bg-primary-900 px-6 py-12 md:px-12 md:py-16">
          <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-primary-700/50" />
          <div className="absolute -bottom-16 right-24 h-56 w-56 rounded-full bg-accent-500/20" />
          <div className="relative max-w-2xl">
            <span className="font-label text-xs tracking-[0.3em] text-accent-400">
              ABOUT TANGXIN
            </span>
            <h1 className="mt-3 text-3xl font-bold text-background-50 font-heading md:text-4xl">
              关于唐心
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-primary-200 md:text-base">
              唐心是一个专注影视资讯与作品推荐的内容平台。我们相信，好的作品值得被更多人看见，而找到它不该是一件费劲的事。唐心做的事很简单——把最新的影视动态、值得追的剧集与电影，认真整理好，送到你面前。
            </p>
          </div>
        </div>
      </section>

      {/* 定位与内容范围 */}
      <section className="w-full px-4 md:px-6 mt-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
          <div>
            <h2 className="text-xl font-bold text-foreground-950 font-heading md:text-2xl">
              我们的定位与内容范围
            </h2>
            <p className="mt-4 text-sm leading-loose text-foreground-600 md:text-base">
              唐心面向广大影视爱好者、剧迷与希望了解时下热度的观众，内容主要覆盖三个方向：
            </p>
            <ul className="mt-5 space-y-4">
              <li className="flex gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-newspaper-line"></i>
                </span>
                <p className="text-sm leading-relaxed text-foreground-700">
                  <strong className="font-semibold text-foreground-900">影视资讯：</strong>
                  行业动态、票房盘点、新片定档、剧集前瞻与幕后专访。
                </p>
              </li>
              <li className="flex gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-tv-2-line"></i>
                </span>
                <p className="text-sm leading-relaxed text-foreground-700">
                  <strong className="font-semibold text-foreground-900">电视剧推荐：</strong>
                  热播剧集与高分口碑剧，附评分、简介与剧情解析。
                </p>
              </li>
              <li className="flex gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                  <i className="ri-film-line"></i>
                </span>
                <p className="text-sm leading-relaxed text-foreground-700">
                  <strong className="font-semibold text-foreground-900">电影推荐：</strong>
                  院线新片与经典佳作，覆盖多种类型与地区。
                </p>
              </li>
            </ul>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {values.map((v, index) => (
              <div
                key={v.title}
                className="rounded-lg bg-background-100 p-5 ring-1 ring-background-200 animate-fade-up"
                style={{ animationDelay: `${index * 80}ms` }}
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-700">
                  <i className={v.icon}></i>
                </span>
                <h3 className="mt-3 text-base font-semibold text-foreground-950">
                  {v.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">
                  {v.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 品牌故事 */}
      <section className="w-full px-4 md:px-6 mt-14">
        <div className="rounded-xl bg-background-100 p-6 ring-1 ring-background-200 md:p-10">
          <h2 className="text-xl font-bold text-foreground-950 font-heading md:text-2xl">
            品牌故事
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <p className="text-sm leading-loose text-foreground-600 md:text-base">
              “唐心”这个名字，取自我们最初的一个朴素想法——做内容要有一副热心肠。我们都是从追剧刷片开始喜欢上影视的普通人，也曾为了找一部好剧翻遍十几个页面。
            </p>
            <p className="text-sm leading-loose text-foreground-600 md:text-base">
              于是，唐心诞生了。我们把散落各处的影视信息整理到一起，配上真实的评分与用心的介绍，希望让“找一部好作品”这件事，变得简单一点、舒服一点。
            </p>
            <p className="text-sm leading-loose text-foreground-600 md:text-base">
              如今，唐心每天仍在更新。我们会继续认真写每一段简介、核每一个评分，也欢迎你通过下方的反馈入口，把想法告诉我们。
            </p>
          </div>
        </div>
      </section>

      {/* 反馈表单 + 联系方式 */}
      <section className="w-full px-4 md:px-6 mt-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <h2 className="text-xl font-bold text-foreground-950 font-heading md:text-2xl">
              联系我们 / 反馈
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              对唐心的内容有任何建议、补充或合作想法，都欢迎留言告诉我们。
            </p>

            <form
              id="tangxin-feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-5 rounded-lg bg-background-100 p-5 ring-1 ring-background-200 md:p-6"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="fb-name"
                    className="mb-1.5 block text-sm font-medium text-foreground-700"
                  >
                    你的称呼
                  </label>
                  <input
                    id="fb-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="w-full rounded-md bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 ring-1 ring-background-200 outline-none placeholder:text-foreground-400 focus:ring-primary-300"
                  />
                </div>
                <div>
                  <label
                    htmlFor="fb-email"
                    className="mb-1.5 block text-sm font-medium text-foreground-700"
                  >
                    邮箱
                  </label>
                  <input
                    id="fb-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-md bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 ring-1 ring-background-200 outline-none placeholder:text-foreground-400 focus:ring-primary-300"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label
                  htmlFor="fb-type"
                  className="mb-1.5 block text-sm font-medium text-foreground-700"
                >
                  反馈类型
                </label>
                <select
                  id="fb-type"
                  name="type"
                  defaultValue="内容建议"
                  className="w-full rounded-md bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 ring-1 ring-background-200 outline-none focus:ring-primary-300 cursor-pointer"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="纠错反馈">纠错反馈</option>
                  <option value="作品推荐">作品推荐</option>
                  <option value="商务合作">商务合作</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div className="mt-4">
                <label
                  htmlFor="fb-message"
                  className="mb-1.5 block text-sm font-medium text-foreground-700"
                >
                  留言内容
                </label>
                <textarea
                  id="fb-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请填写你的建议或想法（最多 500 字）"
                  className="w-full resize-none rounded-md bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 ring-1 ring-background-200 outline-none placeholder:text-foreground-400 focus:ring-primary-300"
                ></textarea>
              </div>

              {/* anti-spam honeypot */}
              <input
                type="text"
                name="website_alt"
                className="form-field-optional-alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status === "success" ? (
                <div className="mt-4 flex items-center gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-checkbox-circle-line text-lg"></i>
                  {successMsg}
                </div>
              ) : null}
              {status === "error" ? (
                <div className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-800">
                  <i className="ri-error-warning-line text-lg"></i>
                  {formError}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700 transition-colors cursor-pointer disabled:opacity-60"
              >
                <i className="ri-send-plane-line"></i>
                {status === "submitting" ? "提交中…" : "提交反馈"}
              </button>
            </form>
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-lg bg-primary-900 p-6 text-background-100">
              <h3 className="text-base font-semibold text-background-50">
                联系方式
              </h3>
              <div className="mt-4 space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-800 text-accent-400">
                    <i className="ri-mail-line"></i>
                  </span>
                  <div>
                    <p className="text-primary-300">邮箱</p>
                    <p className="text-background-50">hello@tangxin.example</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-800 text-accent-400">
                    <i className="ri-weibo-line"></i>
                  </span>
                  <div>
                    <p className="text-primary-300">官方社交账号</p>
                    <p className="text-background-50">@唐心影视</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-800 text-accent-400">
                    <i className="ri-time-line"></i>
                  </span>
                  <div>
                    <p className="text-primary-300">内容更新</p>
                    <p className="text-background-50">每日持续更新</p>
                  </div>
                </div>
              </div>
              <Link
                to="/category/资讯"
                className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-accent-400 hover:text-accent-300 transition-colors cursor-pointer"
              >
                浏览影视资讯
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 使用条款及版权声明 */}
      <section className="w-full px-4 md:px-6 mt-14">
        <div className="rounded-lg bg-background-100 p-6 ring-1 ring-background-200 md:p-8">
          <h2 className="text-xl font-bold text-foreground-950 font-heading md:text-2xl">
            使用条款及版权声明
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <h3 className="text-sm font-semibold text-foreground-900">使用条款</h3>
              <p className="mt-2 text-sm leading-loose text-foreground-600">
                唐心提供的内容仅供个人浏览与学习参考。站内影视资讯、作品简介与评分为唐心整理与撰写，用户在浏览时应遵守相关法律法规，不得将本站内容用于商业用途或未经许可的转载。
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground-900">版权声明</h3>
              <p className="mt-2 text-sm leading-loose text-foreground-600">
                本站涉及的影视作品名称、海报及剧照仅用于信息介绍与评论展示，相关权利归原版权方所有。本站鼓励用户支持正版内容，如涉及权利问题请通过上方联系方式与我们取得联系。
              </p>
            </div>
          </div>
          <p className="mt-6 border-t border-background-200 pt-4 text-xs text-foreground-400">
            注：本站为演示项目，页面中的影视资料、评分与评论均为示例内容。
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}